/**
 * @author: lv.chen
 * @date: 2025/3/6
 * @desc: 新特性强提示Bubble组件，用于有新功能上线时，强制提醒交互
 */
import React from 'react';
import classnames from 'classnames';
import { BubbleProps } from 'tea-component';
import { Bubble } from '@home/components/TeaComponents';
import NewFeatureTipsContent from '@user-center/components/NewFeatureTipsContent';
import useTipsView from '@user-center/_hooks/useTipsView';

export interface INewFeatureTipsBubbleCoreProps {
  // 新特性强提醒key（对应数据库中记录该强提醒的key）
  newFeatureTipsKey: string;
  // 新特性强提醒是否已准备好展示
  newFeatureTipsReadyShow?: boolean;
  // 新特性强提醒的提示内容
  newFeatureTipsContent?: string | React.ReactElement | null;
  // 普通提醒（鼠标hover的时候的提醒，强提醒存在时，普通提醒失效）
  commonTipsContent?: string | React.ReactElement | null;
}

export interface INewFeatureTipsBubbleProps
  extends IBaseProps,
  Omit<BubbleProps, 'visible' | 'content' | 'children'>,
  INewFeatureTipsBubbleCoreProps {}

const NewFeatureTipsBubble: React.FC<INewFeatureTipsBubbleProps> = (props) => {
  const {
    style,
    className,
    overlayStyle,
    dark = true,
    placement = 'top',
    flipped = false,
    escapeWithReference = true,
    arrowPointAtCenter = true,
    children,
    newFeatureTipsKey = '',
    newFeatureTipsContent,
    newFeatureTipsReadyShow = true,
    commonTipsContent,
    ...restProps
  } = props;

  const { maxWidth = 450 } = style || {};

  const { zIndex: overlayZIndex = 1 } = overlayStyle || {};

  const {
    // 是否已查看新特性强提醒
    isTipsView,
    // 关闭新特性强提醒气泡（代表确认已查看新特性强提醒）
    handleViewedTips,
  } = useTipsView(newFeatureTipsKey);

  // 计算气泡内容
  const getBubbleContent = () => {
    if (isTipsView) {
      return commonTipsContent;
    }
    if (newFeatureTipsReadyShow) {
      return <NewFeatureTipsContent description={newFeatureTipsContent} onClose={handleViewedTips} />;
    }
    return undefined;
  };

  return (
    <Bubble
      {...restProps}
      style={{ ...style, maxWidth }}
      overlayStyle={{ ...overlayStyle, zIndex: overlayZIndex }}
      className={classnames('dbu-bubble-max-width dbu-new-feature-tips-bubble', className)}
      dark={dark}
      placement={placement}
      flipped={flipped}
      escapeWithReference={escapeWithReference}
      visible={!isTipsView ? newFeatureTipsReadyShow : undefined}
      arrowPointAtCenter={arrowPointAtCenter}
      content={getBubbleContent()}
    >
      {children}
    </Bubble>
  );
};

export default NewFeatureTipsBubble;
